'use client'

import { FC, ReactNode } from 'react'
import { testIds } from '@/root/shared-consts'
import { useGetTranslations } from '@/root/shared-hooks'
import { Banner } from '@/root/ui/shared/Banner/Banner'
import { Severity } from '@/root/shared-types/ui/ui-types'
import { ToggleButton } from '@/root/modules/shared/SelectTicketPage'
import { TrashBinIcon } from '@/root/ui/shared/icons/TrashBinIcon'
import { Spinner } from '@/root/ui/shared/Spinner/Spinner'
import cn from 'classnames'

interface PassengerCardProps {
  children: ReactNode
  isOpen: boolean
  isResident: boolean
  onToggle: () => void
  variant: Severity
  disabled?: boolean
  firstName?: string
  isDeleting?: boolean
  lastName?: string
  onDelete?: () => void
  personalId?: string
  personalIdOnly?: boolean
}

export const PassengerCard: FC<PassengerCardProps> = ({
  variant,
  children,
  firstName,
  lastName,
  personalId,
  isOpen,
  onToggle,
  onDelete,
  isResident,
  disabled,
  personalIdOnly = false,
  isDeleting = false,
}) => {
  const { getTranslation } = useGetTranslations()

  const switcherText = isOpen ? getTranslation('selectTicketsHide') : getTranslation('selectTicketsShow')
  const showPersonalId = personalId ? personalId : getTranslation('selectTicketsNoPersonalId')
  const showName = firstName && lastName ? `${firstName} ${lastName}` : getTranslation('selectTicketsPassengerStatus')

  return (
    <Banner
      variant={variant}
      className={cn(
        'relative w-full text-left',
        personalIdOnly && 'pt-2',
        personalIdOnly && 'pb-2'
      )}
    >
      {onDelete && (
        <button
          type="button"
          aria-label="Delete passenger"
          aria-busy={isDeleting}
          data-testid={testIds.tickets.passengerDelete}
          disabled={disabled || isDeleting}
          className={cn(
            'absolute -inset-s-2 -top-2 z-10 flex size-5 items-center justify-center rounded-full bg-error',
            disabled || isDeleting ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          onClick={onDelete}
        >
          {isDeleting ? (
            <Spinner className="size-3 border-2 border-white/40 border-t-white" />
          ) : (
            <TrashBinIcon aria-hidden width={10} height={11} color="white" />
          )}
        </button>
      )}
      {!personalIdOnly && (isResident ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex gap-2">
              {!isOpen && (
                <>
                  <p className="text-neutral-darker">{getTranslation('selectTicketsPersonalId')}:</p>
                  <span
                    className={cn({
                      'text-error': variant === 'error',
                      'text-success-darker font-bold': variant === 'success',
                    })}
                  >
                    {showPersonalId}
                  </span>
                </>
              )}
            </div>
            <ToggleButton
              isOpen={isOpen}
              label={switcherText}
              disabled={disabled}
              onClick={onToggle}
              data-testid={testIds.tickets.passengerFormToggle}
            />
          </div>

          {!isOpen && !personalIdOnly && (
            <div className="flex gap-2 text-sm">
              <p className="text-neutral-darker">{getTranslation('selectTicketsPassengerLabel')}:</p>
              <span
                className={cn({
                  'text-error': variant === 'error',
                  'text-success-darker font-bold': variant === 'success',
                })}
              >
                {showName}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between text-sm">
          <div className="flex gap-2">
            <p className="text-neutral-darker">{getTranslation('selectTicketsPassengerLabel')}:</p>
            <span
              className={cn({
                'text-error': variant === 'error',
                'text-success-darker font-bold': variant === 'success',
              })}
            >
              {showName}
            </span>
          </div>
          <ToggleButton
            isOpen={isOpen}
            label={switcherText}
            disabled={disabled}
            onClick={onToggle}
            data-testid={testIds.tickets.passengerFormToggle}
          />
        </div>
      ))}

      {(personalIdOnly || isOpen) && children}
    </Banner>
  )
}
